import { useEffect, useState } from 'react';

import { useLocation, useParams } from 'react-router';

import { skipToken, useQuery } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { z } from 'zod';

import { contentReferenceToDocument } from './helpers';

import {
  ContentReference,
  getResourceOptions,
  getResourceQueryKey,
  getSqsslOptions,
  Resource,
} from '@/_generated/api-clients/xp';
import {
  documentFromDashboardOrResourceAtom,
  documentPersistedAtom,
  setDocumentAtom,
  updateDocumentPersistedAtom,
} from '@/atoms/document-persisted';
import { userPrefsAtom } from '@/atoms/userPrefs';

const documentParamsSchema = z.object({
  documentId: z.string(),
  isCompliance: z
    .string()
    .default('false')
    .transform(val => val === 'true'),
});

/** Document params coming from route. */
export const useDocumentParams = () => {
  const params = useParams();
  const location = useLocation();
  const search = () => {
    const queryParams = new URLSearchParams(location.search);
    const searchParams: Record<string, string> = {};
    queryParams.forEach((value, key) => {
      searchParams[key] = value;
    });
    return searchParams;
  };

  const parsedParams = documentParamsSchema.safeParse({
    ...params,
    ...search(),
  });

  if (!parsedParams.success) {
    throw new Error(
      `Document Route Params Error: ${JSON.stringify(z.treeifyError(parsedParams.error))}`,
    );
  }

  return parsedParams.data;
};

/** Select and find document or fetch document if none in state. */
export const useDocument = (routeDocumentId: string, isCompliance: boolean) => {
  const { chooseDocumentVersion } = useFlags();
  const [selection, setSelection] = useState<{
    reference: ContentReference;
    resource: Resource;
  }>();
  const documentState = useAtomValue(documentFromDashboardOrResourceAtom);
  const { country: userCountry } = useAtomValue(userPrefsAtom);
  const needFetchResource = documentState?.id !== routeDocumentId;

  const setDocument = useSetAtom(setDocumentAtom);
  const updateDocument = useSetAtom(updateDocumentPersistedAtom);
  const { resource, error, isFetching } = useDocumentResource(
    routeDocumentId,
    needFetchResource && !isCompliance,
  );

  /**
   * temporary flag to control whether or not filter references to user country
   * (if endpoint always return all country resources for admin)
   **/
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const activeReferences = !resource?.references
    ? []
    : chooseDocumentVersion
      ? resource.references
      : resource.references.filter(({ country }) => country === userCountry.id);

  // auto choose reference if only one
  useEffect(() => {
    if (!resource?.references) {
      return;
    }

    if (activeReferences.length > 1) {
      updateDocument({ icon: resource.icon });
    }

    if (activeReferences.length === 1 && activeReferences[0]) {
      setSelection({ reference: activeReferences[0], resource });
    }
  }, [resource, updateDocument, activeReferences]);

  // set document based on chosen reference
  useEffect(() => {
    if (!selection) {
      return;
    }

    const document = contentReferenceToDocument(
      selection.reference,
      routeDocumentId,
      selection.resource,
    );

    document.type = selection.resource.type;

    setDocument({
      document,
      routedFrom: 'link',
      icon: selection.resource.icon,
      country: selection.reference.country,
    });
  }, [selection, setDocument, routeDocumentId]);

  const selectReference = (ref: ContentReference) => {
    if (resource) {
      setSelection({ reference: ref, resource });
    }
  };

  const chooseReference = {
    references: activeReferences,
    selectReference,
    modalOpen:
      selection === undefined &&
      activeReferences.length > 1 &&
      needFetchResource,
  };

  const persistedState = useAtomValue(documentPersistedAtom);

  return {
    ...documentState,
    icon: resource?.icon ?? persistedState.icon,
    error,
    isFetching,
    chooseReference,
  };
};

export const useDocumentResource = (
  documentId: string | undefined,
  fetchResource: boolean,
) => {
  const { country: userCountry, language: userLanguage } =
    useAtomValue(userPrefsAtom);

  /**
   * if is Admin, Stakeholder or operator,
   * GetResource will ignore country in param and return all countries' resources
   **/

  const { data, error, ...rest } = useQuery(
    documentId && userLanguage && userCountry.id && fetchResource
      ? getResourceOptions({
          path: { id: documentId },
          query: { country: userCountry.id, language: userLanguage },
        })
      : {
          queryKey: getResourceQueryKey({
            path: { id: '' },
            query: { country: userCountry.id, language: userLanguage },
          }),
          queryFn: skipToken,
        },
  );

  return {
    resource: data,
    error,
    ...rest,
  };
};

export const useComplianceCourse = (courseId: string) => {
  const { data: complianceUrl, error } = useQuery({
    ...getSqsslOptions({ path: { courseId } }),
  });

  return {
    complianceUrl: complianceUrl?.url,
    error: error,
  };
};
